import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import { hexToRgba } from '../../theme/tokens';
import Icon, { iconFor } from './Icon';

// The big number tiles at the top of the dashboard.
export interface KpiCardProps {
  label?: ReactNode;
  value?: ReactNode;
  icon?: ReactNode;
  color?: string;
  backgroundColor?: string;
  style?: CSSProperties;
  isDarkMode?: boolean;
}

const KpiCard = ({ label, value, icon, color, backgroundColor, style, isDarkMode }: KpiCardProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;

  // A flat card: an icon chip tinted with the accent, a small label, one big
  // number. The accent is a tint and a left rule, never a fill.
  const accent = color || t.primary;
  return (
    <div style={{
      backgroundColor: backgroundColor || t.bgMain,
      padding: t.spacing.lg,
      borderRadius: t.radius.lg,
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.md,
      border: `1px solid ${t.border}`,
      borderLeft: `3px solid ${accent}`,
      boxShadow: t.shadowCard,
      minWidth: 0,
      ...style
    }}>
      {icon && (
        <div className="notranslate" translate="no" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '42px', height: '42px', flexShrink: 0,
          fontSize: '20px',
          backgroundColor: hexToRgba(accent, t.isDarkMode ? 0.22 : 0.12),
          borderRadius: t.radius.md
        }}>
          {typeof icon === 'string' ? <Icon name={iconFor(icon)} size={22} style={{ color: accent }} /> : icon}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: t.fontSize.sm, color: t.textMuted, fontWeight: t.fontWeight.bold,
          textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>
          {label}
        </div>
        <div style={{ fontSize: t.fontSize.display, color: t.textMain, fontWeight: t.fontWeight.black, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </div>
      </div>
    </div>
  );
};

export default KpiCard;
